<template>
  <div>
    <!-- Loading with no data: show skeleton -->
    <div v-if="isLoadingAny && !applicationsStore.applications.length">
      <AllApplicationsTableSkeleton />
    </div>

    <!-- Data Table -->
    <div v-else>
      <div class="w-full overflow-x-auto transition-all duration-300" :class="{ 'opacity-50 pointer-events-none': isLoadingAny }">
        <table class="w-full divide-y divide-gray-200 min-w-[1400px]">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th
                v-if="!applicationExpanded"
                class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[100px]"
                @click="handleSort('updated_at')"
              >
                <div class="flex items-center">
                  Updated
                  <TableSortIndicator field="updated_at" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!applicationExpanded"
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[300px] cursor-pointer hover:bg-gray-100"
                @click="handleSort('user__first_name')"
              >
                <div class="flex items-center">
                  Candidate
                  <TableSortIndicator field="user__first_name" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[250px] cursor-pointer hover:bg-gray-100"
                @click="handleSort('open_role__role_name')"
              >
                <div class="flex items-center">
                  Role
                  <TableSortIndicator field="open_role__role_name" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!applicationExpanded"
                class="py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('open_role__employer__name')"
              >
                <div class="flex items-center">
                  Employer
                  <TableSortIndicator field="open_role__employer__name" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!applicationExpanded"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('application_status')"
              >
                <div class="flex items-center">
                  Application Status
                  <TableSortIndicator field="application_status" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!applicationExpanded"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('referral_type')"
              >
                <div class="flex items-center">
                  Referral Type
                  <TableSortIndicator field="referral_type" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!applicationExpanded"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('referral_status')"
              >
                <div class="flex items-center">
                  Referral Status
                  <TableSortIndicator field="referral_status" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
              <th
                v-if="!applicationExpanded"
                class="py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] text-left cursor-pointer hover:bg-gray-100"
                @click="handleSort('interview_status')"
              >
                <div class="flex items-center">
                  Interview Stage
                  <TableSortIndicator field="interview_status" :current-sort="applicationsStore.currentSort" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="application in applicationsStore.applications"
              :key="application.id"
              @click="onRowClick(application, $event)"
              class="hover:bg-gray-50 cursor-pointer"
              :class="{ 'bg-gray-100': currentApplicationId === Number(application.id) }"
            >
              <!-- Updated Date -->
              <td
                v-if="!applicationExpanded"
                class="px-2 py-4 whitespace-nowrap text-gray-900 text-xs cursor-pointer w-[100px]"
              >
                {{ formatDate(application.updated_at, 'europe') }}
              </td>

              <!-- Candidate -->
              <td
                v-if="!applicationExpanded"
                class="px-4 py-4 cursor-pointer overflow-hidden min-w-[300px]"
              >
                <div v-if="application.user_details" class="flex flex-col truncate">
                  <UserDetailsSmall :user_details="application.user_details" />
                </div>
              </td>

              <!-- Role -->
              <td
                class="pl-4 pr-4 py-4 cursor-pointer overflow-hidden min-w-[250px]"
                :class="{ 'py-3 px-4': applicationExpanded }"
              >
                <div class="min-w-0">
                  <div v-if="applicationExpanded" class="flex gap-2 mb-2">
                    <img
                      v-if="application.user_details"
                      :src="application.user_details.avatar_image || '/profile_photo.jpg'"
                      class="h-4 w-4 aspect-square rounded-full object-cover"
                      alt="Profile"
                    />
                    <div
                      v-else
                      class="h-4 w-4 aspect-square rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm"
                    >
                      <User class="w-4 h-4" />
                    </div>
                    <span class="text-xs text-gray-600 block truncate">
                      {{ application.user_details?.first_name }}
                      {{ application.user_details?.last_name }}
                    </span>
                  </div>
                  <div class="flex items-center">
                    <div
                      class="text-sm font-medium text-gray-900 truncate max-w-[350px]"
                      :title="application.open_role_details?.role_name"
                    >
                      {{ application.open_role_details?.role_name }}
                    </div>
                  </div>
                  <div class="flex items-center gap-2 min-w-0 mt-1">
                    <LocationDisplay :data="application" text-size="xs" />
                  </div>
                </div>
              </td>

              <!-- Employer -->
              <td v-if="!applicationExpanded" class="whitespace-nowrap px-2">
                <div class="flex items-center min-w-0">
                  <img
                    v-if="application.open_role_details?.employer_details?.logo"
                    :src="application.open_role_details?.employer_details?.logo"
                    class="h-8 w-8 rounded-lg mr-3 flex-shrink-0"
                  />
                  <div
                    v-else
                    class="h-8 w-8 rounded-lg bg-gray-200 mr-3 flex items-center justify-center flex-shrink-0"
                  >
                    <Building2 class="h-4 w-4 text-gray-400" />
                  </div>
                  <div class="text-sm text-gray-500 truncate">
                    {{ application.open_role_details?.employer_details?.name }}
                  </div>
                </div>
              </td>

              <!-- Application Status -->
              <td v-if="!applicationExpanded" class="whitespace-nowrap px-2" @click.stop>
                <JobApplicationStatusDropdown
                  :model-value="application.application_status ?? ''"
                  status-type="application"
                  :show-label="false"
                  :show-comment="false"
                  :editable="true"
                  :loading="isCellLoading(application.id, 'application_status')"
                  @update:model-value="(value) => handleStatusFieldUpdate(application, 'application_status', value)"
                />
              </td>

              <!-- Referral Type -->
              <td v-if="!applicationExpanded" class="whitespace-nowrap px-2" @click.stop>
                <JobApplicationStatusDropdown
                  :model-value="application.referral_type ?? ''"
                  status-type="referralType"
                  :show-label="false"
                  :show-comment="false"
                  :editable="true"
                  :loading="isCellLoading(application.id, 'referral_type')"
                  @update:model-value="(value) => handleStatusFieldUpdate(application, 'referral_type', value)"
                />
              </td>

              <!-- Referral Status -->
              <td v-if="!applicationExpanded" class="whitespace-nowrap px-2" @click.stop>
                <JobApplicationStatusDropdown
                  :model-value="application.referral_status ?? ''"
                  status-type="referralStatus"
                  :show-label="false"
                  :show-comment="false"
                  :editable="true"
                  :loading="isCellLoading(application.id, 'referral_status')"
                  @update:model-value="(value) => handleStatusFieldUpdate(application, 'referral_status', value)"
                  :minimized="true"
                />
              </td>

              <!-- Interview Stage -->
              <td v-if="!applicationExpanded" class="whitespace-nowrap px-2" @click.stop>
                <JobApplicationStatusDropdown
                  :model-value="application.interview_status ?? ''"
                  status-type="interview"
                  :show-label="false"
                  :show-comment="false"
                  :editable="true"
                  :loading="isCellLoading(application.id, 'interview_status')"
                  @update:model-value="(value) => handleStatusFieldUpdate(application, 'interview_status', value)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <PaginationComponent
        v-if="applicationsStore.applications.length"
        :current-page="applicationsStore.currentPage"
        :total-pages="totalPages"
        :use-router="true"
        :additional-query-params="{
          search: applicationsStore.searchQuery || undefined,
          status: applicationsStore.selectedStatus || undefined,
          ordering: applicationsStore.currentSort
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router'
import { useApplicationsStore } from '@/stores/applications'
import { User, Building2 } from 'lucide-vue-next'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import JobApplicationStatusDropdown from '@/components/common/jobApplication/JobApplicationStatusDropdown.vue'
import { formatDate } from '@/utils/formatDate'
import LocationDisplay from '@/components/common/LocationDisplay.vue'
import AllApplicationsTableSkeleton from '@/components/management/AllApplicationsTableSkeleton.vue'
import UserDetailsSmall from '@/components/common/UserDetailsSmall.vue'
import TableSortIndicator from '@/components/common/TableSortIndicator.vue'

// Define component name for KeepAlive
defineOptions({
  name: 'AllApplicationsTable'
})

const router = useRouter()
const route = useRoute()
const applicationsStore = useApplicationsStore()
defineProps<{
  applicationExpanded: boolean
}>()

const pageSize = 50

const currentApplicationId = computed(() => {
  const id = route.params?.application_id ? Number(route.params.application_id) : null
  return id
})

const totalPages = computed(() => Math.ceil(applicationsStore.totalCount / pageSize))

// Unified loading state for dimming table when data exists
const isLoadingAny = computed(() => applicationsStore.isLoadingList)

// Per-cell loading state helper
const cellLoading = ref<Record<string, boolean>>({})
const keyFor = (id: number | string, field: string) => `${id}-${field}`
const isCellLoading = (id: number | string, field: 'application_status' | 'referral_type' | 'referral_status' | 'interview_status') => {
  return !!cellLoading.value[keyFor(id, field)]
}

const handleSort = (field: string) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicationsStore.updateSort(field, router)
}

const onRowClick = (application: any, event: MouseEvent) => {
  const targetRoute: RouteLocationRaw = {
    path: `/applications/all/${application.id}`,
    query: { ...route.query }
  }

  if (event.metaKey || event.ctrlKey) {
    // Open in new tab for Cmd/Ctrl+Click
    const resolvedRoute = router.resolve(targetRoute)
    window.open(resolvedRoute.href, '_blank')
  } else {
    // Navigate in the current tab for normal click
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(targetRoute)
  }
}

type StatusField = 'application_status' | 'referral_type' | 'referral_status' | 'interview_status'

const handleStatusFieldUpdate = async (application: any, field: StatusField, newValue: string) => {
  const k = keyFor(application.id, field)
  if (cellLoading.value[k]) return
  const previous = application[field]
  // Optimistic update
  application[field] = newValue
  cellLoading.value[k] = true
  try {
    await applicationsStore.updateApplication(application.id, { [field]: newValue as any })
    
    // Trigger job matching if referral status is changed to READY_FOR_REFERRAL
    if (field === 'referral_status' && newValue === 'READY_FOR_REFERRAL') {
      applicationsStore.matchResumeToJob(application.id).catch(err => {
        console.error('Failed to trigger job matching:', err)
        // We don't show a toast here to avoid spamming if multiple are updated, 
        // but we log the error. The user will see the state in the details view.
      })
    }
  } catch (error) {
    // Revert on failure
    application[field] = previous
    console.error(`Error updating ${field}:`, error)
  } finally {
    cellLoading.value[k] = false
  }
}

// Watch for route changes
watch(
  [() => route.query, () => route.params.application_id],
  // eslint-disable-next-line complexity
  ([newQuery, newApplicationId], [oldQuery, oldApplicationId]) => {
    // Update current page from URL if it changed
    const page = Number(newQuery.page) || 1
    if (page !== applicationsStore.currentPage) {
      applicationsStore.currentPage = page
    }

    // Update sort from URL if it changed
    if (newQuery.ordering && typeof newQuery.ordering === 'string') {
      if (newQuery.ordering !== applicationsStore.currentSort) {
        applicationsStore.currentSort = newQuery.ordering
      }
    }

    if (!newApplicationId || (oldApplicationId && !newApplicationId)) {
      // Handle filters - this must mirror the logic in the router beforeEnter guard
      if (newQuery.status && typeof newQuery.status === 'string') {
        applicationsStore.selectedStatus = newQuery.status as string
      } else {
        applicationsStore.selectedStatus = ''
      }
    }

    // Fetch if:
    // 1. The page number has changed (always fetch on page change)
    // 2. OR if we're not in an application view and search/status/ordering changed
    if (
      newQuery.page !== oldQuery?.page || // Always fetch on page change
      (!newApplicationId &&
        !oldApplicationId && // Only fetch on filter changes if not in application view
        (newQuery.search !== oldQuery?.search || 
         newQuery.status !== oldQuery?.status || 
         newQuery.ordering !== oldQuery?.ordering))
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      applicationsStore.fetchApplications()
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
